import { AlertCircle } from 'lucide-react';

/**
 * Tell-on-click per-field validation helpers (#5857).
 *
 * Pattern (reference: campaign/assets/v0/price-section.tsx):
 * - The submit button stays ENABLED (disable only while a request is in flight)
 * - Clicking submit builds a per-field error map; each missing/invalid field gets
 *   a red border (`error` prop on Input/Textarea), an AlertCircle inside the
 *   input's right edge, and a short red message on the label row
 * - A field's error clears as soon as that field is edited; all errors reset when
 *   the modal opens
 *
 * Usage:
 *   <div className="flex items-center justify-between">
 *     <Label>Header</Label>
 *     <FieldError message={fieldErrors.header} />
 *   </div>
 *   <div className="relative mt-1">
 *     <Input error={!!fieldErrors.header} className="pr-8" ... />
 *     <InputAdornment error={!!fieldErrors.header} />
 *   </div>
 *
 * With a character counter, widen the padding (pr-20) and pass the counter as a child:
 *   <InputAdornment error={!!fieldErrors.header}>
 *     <CharacterCounter current={value.length} max={25} variant="compact" />
 *   </InputAdornment>
 */

/** Short red validation message for a field's label row, right-aligned via the label row's justify-between. */
export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <span className="text-xs text-red-500">{message}</span>;
}

/**
 * Absolute right-edge adornment slot for an input wrapped in a `relative` container:
 * shows the error icon and any extras (e.g. CharacterCounter) vertically centred.
 */
export function InputAdornment({ error, children }: { error?: boolean; children?: React.ReactNode }) {
  if (!error && !children) return null;
  return (
    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1.5">
      {error && <AlertCircle className="h-4 w-4 text-red-500" />}
      {children}
    </div>
  );
}
